import { FC, useEffect, useState } from 'react'
import {
  Body1,
  Box,
  Button,
  DialogTrigger,
  formatPhoneNumber,
  IconButton,
  Link,
  makeStyles,
  MuiStack,
  Tooltip,
  Typography,
} from '@perk-ui/core'
import { Mail, MessageSquare } from 'react-feather'
import { useQueryClient } from 'react-query'
import { useHistory, useRouteMatch } from 'react-router'

import surveyWelcomeLogo from '../../../assets/SurveyWelcome.png'
import { useSitesContext } from '../../../components/SitesProvider'
import QueryKeys from '../../../features/query-hooks/QueryKeys'
import usePatient from '../../../features/query-hooks/usePatient'
import useResendSurveyNotification from '../../../features/query-hooks/useResendSurveyNotification'
import useSurveyResponseGroupsAlt from '../../../features/query-hooks/useSurveyResponseGroupsAlt'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  sectionTitle: {
    fontFamily: 'DM Sans',
    fontSize: '40px',
    fontWeight: 700,
    lineHeight: '40px',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  subTitle: {
    fontFamily: 'DM Sans',
    color: '#737373',
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '20px',
    textAlign: 'center',
    marginBottom: theme.spacing(7),
  },
  imgContainer: {
    padding: theme.spacing(2, 0, 6, 0),
    textAlign: 'center',
  },
  welcomeImg: {
    margin: theme.spacing(2),
    width: 350,
  },
  iFrame: {
    height: 'calc(100dvh - 96px)',
  },
  resendLink: {
    border: '1px solid #56AAE8',
    color: '#56AAE8',
    margin: theme.spacing(0, 1),
    '&.disabled-link': {
      color: '#BDBDBD',
      borderColor: '#BDBDBD',
      cursor: 'not-allowed',
    },
  },
  dialogContent: {},
  dialogButtons: {
    display: 'flex',
    marginTop: theme.spacing(3),
    justifyContent: 'center',
    '& button+button': {
      marginLeft: theme.spacing(5),
    },
  },
  resendContainer: {
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  resendTitle: {
    color: '#56AAE8',
    fontSize: '16px',
    fontWeight: 400,
    marginBottom: theme.spacing(1),
  },
  resendButtons: {
    display: 'flex',
    justifyContent: 'center',
  },
}))

interface SurveyProps {}

const SurveyWelcome: FC<SurveyProps> = () => {
  const classes = useStyles()
  const { push } = useHistory()
  const [hasLoaded, setHasLoaded] = useState(false)
  const queryClient = useQueryClient()
  const { currentSite } = useSitesContext()
  const orgName = currentSite?.organization.name
  const resendSurveyNotification = useResendSurveyNotification()
  const hasCase = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.survey.match,
  )
  const { patientId, caseId } = hasCase?.params || {}
  const { data: openSurveys = [], isLoading } = useSurveyResponseGroupsAlt({
    patientId: patientId,
    patientCaseId: caseId,
  })

  useEffect(() => {
    setTimeout(() => {
      queryClient.invalidateQueries(
        QueryKeys.surveyResponseGroupsAlt({
          currentSiteId: currentSite?.id || '',
          patientId,
          patientCaseId: caseId,
        }),
      )
      setHasLoaded(true)
    }, 3000)
  }, [])
  const { data: patient } = usePatient(patientId)
  const patientPhoneType = patient?.phoneData?.phoneType
    ? `[${patient.phoneData.phoneType}]`
    : ''
  let smsLinkClass = `${classes.resendLink}`
  let isSmsLinkDisabled = false
  let smsDisabledReason = 'Send SMS'
  let emailLinkClass = `${classes.resendLink}`
  let isEmailLinkDisabled = false
  let emailDisabledReason = 'Send Email'
  if (patient) {
    if (patient.isOptedOutPhone || !patient.isValidPhone) {
      smsLinkClass += ` disabled-link`
      smsLinkClass += ` ${classes.resendLink}`
      isSmsLinkDisabled = true
      if (patient.isOptedOutPhone && !patient.isValidPhone) {
        smsDisabledReason =
          "Phone number can't receive SMS messages and has also opted-out"
      } else if (!patient.isValidPhone) {
        smsDisabledReason = "Phone number can't receive SMS messages"
      } else if (patient.isOptedOutPhone) {
        smsDisabledReason = 'Phone number has opted-out for SMS messages'
      }
    }
    if (!patient.email) {
      emailLinkClass += ` disabled-link`
      isEmailLinkDisabled = true
      emailDisabledReason = 'No email entered.'
    }
  }

  const handleSmsClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'sms',
    })
  }
  const handleEmailClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'email',
    })
  }

  return (
    <Box className={classes.root}>
      <MuiStack justifyContent="center">
        {openSurveys.length > 0 && hasLoaded && !isLoading && (
          <Typography className={classes.sectionTitle}>
            Hand the device to the patient
          </Typography>
        )}
        {openSurveys.length > 0 && hasLoaded && !isLoading && (
          <Typography className={classes.subTitle}>
            Click on get started to complete the survey
          </Typography>
        )}
        <Box className={classes.imgContainer}>
          <img className={classes.welcomeImg} src={surveyWelcomeLogo} />
        </Box>
        <Box sx={{ padding: '16px' }}>
          <Button
            fullWidth
            disabled={isLoading || !hasLoaded}
            onClick={() => {
              if (openSurveys.length == 0) {
                push(
                  PatientSelfOnboardUrls.existingPatientDetails.create(
                    patientId || '',
                  ),
                )
              } else {
                push(
                  PatientSelfOnboardUrls.surveyCompletion.create(
                    patientId || '',
                    caseId || '',
                  ),
                )
              }
            }}
          >
            {openSurveys.length == 0 && hasLoaded && !isLoading
              ? 'No surveys available'
              : !hasLoaded || isLoading
              ? 'Loading...'
              : 'Start Pre-Treatment Survey'}
          </Button>
        </Box>
        {/* This section is removed */}
        {openSurveys.length > 0 && orgName === 'EmCyte' && false && (
          <Box className={classes.resendContainer}>
            <Box className={classes.resendTitle}>Send to</Box>

            <Box className={classes.resendButtons}>
              <DialogTrigger
                disabled={isSmsLinkDisabled}
                action={
                  <Tooltip arrow placement="top" title={smsDisabledReason}>
                    <span>
                      <Link component={IconButton} className={smsLinkClass}>
                        <MessageSquare />
                      </Link>
                    </span>
                  </Tooltip>
                }
                content={(toggle) => (
                  <Box p={3} className={classes.dialogContent}>
                    <Body1 mb={2}>
                      Are you sure you want to send this Survey link?
                    </Body1>
                    <Body1>
                      {patient?.phoneData?.phoneType !== 'MOBILE'
                        ? `${formatPhoneNumber(
                            patient?.cellPhoneLocal || '',
                          )} ${patientPhoneType} may not be able to receive SMS messages. Click Cancel, or click Confirm to send anyway.`
                        : ''}
                    </Body1>
                    <div className={classes.dialogButtons}>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() => toggle()}
                      >
                        Cancel
                      </Button>

                      <Button
                        size="medium"
                        onClick={() =>
                          handleSmsClick(openSurveys[0].id).then(() => toggle())
                        }
                        disabled={resendSurveyNotification.isLoading}
                      >
                        Confirm
                      </Button>
                    </div>
                  </Box>
                )}
              />
              <DialogTrigger
                disabled={isEmailLinkDisabled}
                action={
                  <Tooltip arrow placement="top" title={emailDisabledReason}>
                    <span>
                      <Link component={IconButton} className={emailLinkClass}>
                        <Mail />
                      </Link>
                    </span>
                  </Tooltip>
                }
                content={(toggle) => (
                  <Box p={3} className={classes.dialogContent}>
                    <Body1>
                      Are you sure you want to send this Survey link?
                    </Body1>
                    <div className={classes.dialogButtons}>
                      <Button
                        size="medium"
                        variant="outlined"
                        onClick={() => toggle()}
                      >
                        Cancel
                      </Button>

                      <Button
                        size="medium"
                        onClick={() =>
                          handleEmailClick(openSurveys[0].id).then(() =>
                            toggle(),
                          )
                        }
                        disabled={resendSurveyNotification.isLoading}
                      >
                        Confirm
                      </Button>
                    </div>
                  </Box>
                )}
              />
            </Box>
          </Box>
        )}
      </MuiStack>
    </Box>
  )
}
export default SurveyWelcome
